import React, { useId } from "react";
import type { ConditionalPredicate, ConditionalScalar, ConditionalValueType } from "../../nativeConditions.mjs";
import { CONDITION_LIMITS } from "../../nativeConditions.mjs";
import {
  defaultConditionalLiteral, defaultConditionalPredicate, scalarIdentity,
  type ConditionalFieldOption,
} from "../conditionalAuthoring";
import { cloneValue } from "../reportDesignerModel";
import styles from "./ConditionalRulesInspector.module.css";

const comparisonLabels = {
  eq: "Equals", neq: "Does not equal", gt: "Greater than", gte: "Greater than or equal",
  lt: "Less than", lte: "Less than or equal", isNull: "Is null", isNotNull: "Is not null",
};

export default function ConditionalPredicateEditor({ value, fields, onChange, depth = 1, label = "Condition" }: {
  value: ConditionalPredicate; fields: ConditionalFieldOption[]; onChange: (value: ConditionalPredicate) => void;
  depth?: number; label?: string;
}) {
  const id = useId().replace(/:/g, "");
  const mode = "const" in value ? "constant" : value.op === "and" || value.op === "or" ? "group" : value.op === "not" ? "not" : "comparison";
  const canNest = depth + 2 < CONDITION_LIMITS.depth;
  function changeMode(next: string) {
    const seed = defaultConditionalPredicate(fields);
    const current = "const" in value ? seed : cloneValue(value);
    const comparison = "op" in value && value.op === "not" ? value.arg : "op" in value && (value.op === "and" || value.op === "or") ? value.args[0] : seed;
    onChange(next === "constant" ? { const: true } : next === "group" ? { op: "and", args: [current, seed] } : next === "not" ? { op: "not", arg: current } :
      "op" in comparison && !["and", "or", "not"].includes(comparison.op) ? cloneValue(comparison) : seed);
  }
  return (
    <fieldset className={styles["conditional-predicate"]}>
      <legend>{label}</legend>
      <label htmlFor={`conditional${id}Mode`}>Condition type</label>
      <select id={`conditional${id}Mode`} value={mode} onChange={event => changeMode(event.target.value)}>
        <option value="comparison">Field comparison</option>
        <option disabled={!canNest} value="group">AND / OR group</option>
        <option disabled={!canNest} value="not">NOT condition</option>
        <option value="constant">Always / Never</option>
      </select>
      {"const" in value ? (
        <>
          <label htmlFor={`conditional${id}Constant`}>Boolean condition</label>
          <select id={`conditional${id}Constant`} value={String(value.const)} onChange={event => onChange({ const: event.target.value === "true" })}>
            <option value="true">Always</option><option value="false">Never</option>
          </select>
        </>
      ) : value.op === "and" || value.op === "or" ? (
        <>
          <label htmlFor={`conditional${id}Join`}>Match conditions</label>
          <select id={`conditional${id}Join`} value={value.op} onChange={event => onChange({ ...value, op: event.target.value === "and" ? "and" : "or" })}>
            <option value="and">All (AND)</option><option value="or">Any (OR)</option>
          </select>
          {value.args.map((arg, index) => (
            <div className={styles["conditional-nested"]} data-testid={`conditional-group-item-${index}`} key={index}>
              <ConditionalPredicateEditor label={`Condition ${index + 1}`} depth={depth + 1} value={arg} fields={fields} onChange={next => onChange({ ...value, args: value.args.map((candidate, position) => position === index ? next : candidate) })} />
              <button type="button" onClick={() => {
                const args = value.args.filter((_, position) => position !== index);
                onChange(args.length === 1 ? args[0] : { ...value, args });
              }}>Remove condition {index + 1}</button>
            </div>
          ))}
          <button disabled={value.args.length >= CONDITION_LIMITS.operands} type="button" onClick={() => onChange({ ...value, args: [...value.args, defaultConditionalPredicate(fields)] })}>Add condition</button>
        </>
      ) : value.op === "not" ? (
        <ConditionalPredicateEditor label="Negated condition" depth={depth + 1} value={value.arg} fields={fields} onChange={arg => onChange({ op: "not", arg })} />
      ) : "left" in value || "arg" in value ? (
        <ComparisonEditor id={id} value={value} fields={fields} onChange={onChange} />
      ) : null}
    </fieldset>
  );
}

type Comparison = Extract<ConditionalPredicate, { left: ConditionalScalar } | { op: "isNull" | "isNotNull" }>;

function ComparisonEditor({ id, value, fields, onChange }: {
  id: string; value: Comparison; fields: ConditionalFieldOption[]; onChange: (value: ConditionalPredicate) => void;
}) {
  const left = "left" in value ? value.left : value.arg;
  const key = scalarIdentity(left);
  const option = fields.find(field => field.key === key);
  const type = option?.type || ("field" in left ? left.valueType : "aggregate" in left ? "number" : undefined);
  const operators = Object.entries(comparisonLabels).filter(([op]) => op === "isNull" || op === "isNotNull" || (type && (["eq", "neq"].includes(op) || ["number", "date", "timestamp"].includes(type))));
  const right = "right" in value ? value.right : defaultConditionalLiteral(type);
  function comparison(op: string, scalar = left, literal = right) {
    if (op === "isNull" || op === "isNotNull") onChange({ op, arg: scalar });
    else if (op === "eq" || op === "neq" || op === "gt" || op === "gte" || op === "lt" || op === "lte") onChange({ op, left: scalar, right: literal });
  }
  return (
    <>
      <label htmlFor={`conditional${id}Field`}>Field or aggregate</label>
      <select id={`conditional${id}Field`} value={key} onChange={event => {
        const next = fields.find(field => field.key === event.target.value);
        if (next) comparison(next.type ? "eq" : "isNotNull", cloneValue(next.scalar), next.enumValues?.length ? { const: next.enumValues[0] } : defaultConditionalLiteral(next.type));
      }}>
        {!option && <option value={key}>Unresolved: {"field" in left ? left.field : "existing scalar"} (preserved)</option>}
        {fields.map(field => <option value={field.key} key={field.key}>{field.label}</option>)}
      </select>
      <label htmlFor={`conditional${id}Operator`}>Comparison operator</label>
      <select id={`conditional${id}Operator`} value={value.op} onChange={event => comparison(event.target.value)}>
        {!operators.some(([op]) => op === value.op) && <option value={value.op}>{value.op} (requires typed metadata)</option>}
        {operators.map(([op, label]) => <option value={op} key={op}>{label}</option>)}
      </select>
      {"right" in value && <ScalarValueEditor id={id} value={right} type={type} enumValues={option?.enumValues} fields={fields} onChange={next => comparison(value.op, left, next)} />}
    </>
  );
}

function ScalarValueEditor({ id, value, type, enumValues, fields, onChange }: {
  id: string; value: ConditionalScalar; type?: ConditionalValueType; fields: ConditionalFieldOption[];
  enumValues?: string[];
  onChange: (value: ConditionalScalar) => void;
}) {
  const isLiteral = "const" in value;
  const [input, setInput] = React.useState(isLiteral ? value.const === null ? "" : String(value.const) : "");
  React.useEffect(() => { if ("const" in value) setInput(value.const === null ? "" : String(value.const)); }, [value]);
  return (
    <>
      <label htmlFor={`conditional${id}ValueKind`}>Compare with</label>
      <select id={`conditional${id}ValueKind`} value={isLiteral ? value.const === null ? "null" : "literal" : "field"} onChange={event => onChange(event.target.value === "null" ? { const: null } : event.target.value === "field" ? cloneValue(fields.find(field => field.type === type)?.scalar || value) : enumValues?.length ? { const: enumValues[0] } : defaultConditionalLiteral(type))}>
        <option value="literal">Typed value</option><option value="field">Field or aggregate</option><option value="null">Explicit null</option>
      </select>
      {!isLiteral ? (
        <>
          <label htmlFor={`conditional${id}OtherField`}>Comparison field</label>
          <select id={`conditional${id}OtherField`} value={scalarIdentity(value)} onChange={event => {
            const next = fields.find(field => field.key === event.target.value);
            if (next) onChange(cloneValue(next.scalar));
          }}>
            {!fields.some(field => field.key === scalarIdentity(value)) && <option value={scalarIdentity(value)}>Unresolved binding (preserved)</option>}
            {fields.filter(field => field.type === type).map(field => <option value={field.key} key={field.key}>{field.label}</option>)}
          </select>
        </>
      ) : value.const !== null ? (
        <>
          <label htmlFor={`conditional${id}Value`}>Comparison value</label>
          {enumValues?.length ? (
            <select id={`conditional${id}Value`} value={String(value.const)} onChange={event => onChange({ const: event.target.value })}>
              {!enumValues.includes(String(value.const)) && <option value={String(value.const)}>Existing value: {String(value.const)} (preserved)</option>}
              {enumValues.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
          ) : type === "boolean" ? (
            <select id={`conditional${id}Value`} value={String(value.const)} onChange={event => onChange({ const: event.target.value === "true" })}>
              <option value="true">True</option><option value="false">False</option>
            </select>
          ) : (
            <input id={`conditional${id}Value`} type={type === "number" ? "number" : type === "date" ? "date" : "text"} step={type === "number" ? "any" : undefined} value={input} onChange={event => {
              const text = event.target.value;
              setInput(text);
              onChange(type === "date" || type === "timestamp" ? { const: text, valueType: type } :
                { const: type === "number" && text.trim() !== "" && Number.isFinite(Number(text)) ? Number(text) : text });
            }} />
          )}
          {type === "date" && <small>Calendar date only; no timezone conversion.</small>}
          {type === "timestamp" && <small>Use an ISO timestamp with an explicit timezone.</small>}
        </>
      ) : null}
    </>
  );
}
